import { EmberRosterRole, Entity, TileSize } from "game-common/models"
import { Callback, chunkSentence } from "game-common/util"
import { BitmapText, Container, Graphics, LINE_JOIN, Rectangle, Text, TextMetrics } from "pixi.js"

import { EntityRenderMeta } from "../entity_rendering_manager"
import { Fader } from "../gui/fader"
import { BaseRenderable, SpeechOptions } from "./base_renderable"
import { BasicSprite } from "./basic_sprite"
import { TextRenderer } from "../text/text_renderer"
import { DyanmicTextContainer } from "../gui/dynamic_text_container"
import { SpeechHoldButtons } from "../gui/speech_hold"

// Chat speech-bubble geometry (see spokeImpl)
const BUBBLE_PAD_X = 12
const BUBBLE_PAD_Y = 10
const BUBBLE_RADIUS = 8
const BUBBLE_BORDER = 2
const BUBBLE_SHADOW_X = 3
const BUBBLE_SHADOW_Y = 4
const BUBBLE_FILL = 0x141821
const BUBBLE_FILL_ALPHA = 0.9
const BUBBLE_BORDER_COLOR = 0x707b91
const BUBBLE_BORDER_ALPHA = 0.75

// The "Next" button on a HELD bubble (see spoke): a line the player dismisses themselves rather
// than one that fades on a timer. It hangs off the bottom-right inside edge of the panel, on its own
// row, so it can never sit over the words however they wrap.
const NEXT_BUTTON_W = 52
const NEXT_BUTTON_H = 19 // tall enough for the label plus the countdown bar's own row
const NEXT_BUTTON_CORNER = 5
const NEXT_BUTTON_GAP = 7 // clear air between the last line of text and the button row
const NEXT_BUTTON_FONT = 10
// Warm amber, matching the ember UI set (role picker / tutorial bar / command wheel).
const NEXT_RIM = 0xf2a94e
const NEXT_TEXT = 0xfff3e0
const NEXT_FILL_IDLE = 0x2a201a
const NEXT_FILL_HOVER = 0x33271e
const NEXT_RIM_IDLE = 0x6b4f38

// A held line stops the scene behind it until it is dismissed, so the button presses ITSELF after
// this long: a player who wanders off, misses the button, or can't reach it (a bubble drawn half off
// the screen) must not be able to strand the ember tutorial for good. The countdown bar along the
// bottom of the button is the only cue, and it is deliberately quiet -- the button is still the way
// you're meant to get through a conversation, not a thing to race.
const NEXT_BUTTON_AUTO_MS = 15000
// Redraw cadence of that bar. Coarse on purpose: the bar is ~42px wide, so a finer tick cannot move
// it a whole pixel, and a live bubble has no business churning the GPU on a budget machine.
const NEXT_PROGRESS_TICK_MS = 250
const NEXT_PROGRESS_H = 2
const NEXT_PROGRESS_ROW = 4 // the bar plus its clearance from the button's bottom border
const NEXT_PROGRESS_INSET = NEXT_BUTTON_CORNER // keeps the bar clear of the rounded corners
const NEXT_PROGRESS_ALPHA = 0.45

// Ceiling on the per-entity backlog of lines waiting behind a held bubble. A player who walks away
// mid-conversation never presses Next, and the speaker (the ember Lord, mid-intro) keeps talking;
// without a cap that queue grows for as long as the run lasts. Dropping the OLDEST line keeps the
// most recent, most relevant ones.
const MAX_QUEUED_LINES = 8

// Compact segmented HP meter drawn beneath the name (variant B) -- replaces the old "|" pipe text.
// Colours mirror the roster HP bar (ember_roster drawHpBar) so the world and roster read the same.
const HP_BAR_W = 54
const HP_BAR_H = 7
const HP_SEGMENTS = 6
const HP_SEG_GAP = 2
const HP_CASE_PAD = 2
const HP_COLOR_FULL = 0x6fe06f
const HP_COLOR_WARN = 0xffd23f
const HP_COLOR_CRIT = 0xff5c5c

// Floating role glyph (👑 lord / 🛡️ defender / 🪓 forager) shown above an ember-camp NPC's head. It
// rests just above the head and lifts on hover to make room for the name + HP, then drops back down.
// The Y values are the tunable bits (like the HP-bar alignment was).
const GLYPH_SIZE = 15
const GLYPH_X = 18 // centred over the NPC (tunable)
const GLYPH_Y = 20 // rests just above the head; the glyph fades out on hover (it does not move)
const GLYPH_FADE_MS = 130
// Emoji hardcoded (not imported from gui/ember_roster) to avoid a sprites<->gui import cycle -- the
// same reason command_wheel keeps its own copy. Keep in sync with ROLE_EMOJI in ember_roster_card.ts.
const ROLE_GLYPH: Record<EmberRosterRole, string> = {
    lord: "👑",
    defender: "🛡️",
    forager: "🪓",
}

// Draw order of the per-entity widgets inside the HUD. These only take effect because the HUD turns
// on sortableChildren in the constructor -- without it pixi paints in addChild order, which put the
// role glyph and the sigil (added later, on demand) on top of the chat bubble.
const Z_TAG = 0
const Z_LABEL = 10 // name + HP meter
const Z_SIGIL = 20
const Z_ROLE_GLYPH = 30
const Z_POINTER = 40
const Z_CHAT_BUBBLE = 100000 // a bubble always reads over every other widget of its entity
// Every entity's HUD is a separate child of the world-overlay surface, so ordering the bubble within
// its own HUD isn't enough -- a neighbour's nameplate/glyph would still paint over it. The speaking
// entity's whole overlay item is lifted above the resting ones for as long as its bubble is up.
const Z_OVERLAY_RESTING = 0
const Z_OVERLAY_SPEAKING = 1000

// interface Job {
//     run: (done: Callback<void>) => void
// }

// class JobQueue {
//     private queue: Job[] = []
//     private running: boolean = false

//     constructor() {
//         //
//     }

//     add = (callback: Job) => {
//         if (this.queue.length > 2) {
//             this.queue.shift()
//         }
//         this.queue.push(callback)
//         if (!this.running) {
//             this.run()
//         }
//     }

//     private run = () => {
//         this.running = true
//         const callback = this.queue.shift()
//         if (callback) {
//             callback.run(() => this.run())
//         } else {
//             this.running = false
//         }
//     }
// }

export class AvatarHud extends BaseRenderable {
    id: string
    idText: Text
    hpBar: Graphics
    currentName: string = ""
    currentLevel: number
    currentColor: number
    currentHp: number = 0
    currentHpMax: number = 0

    roleGlyph?: Text
    currentRole: EmberRosterRole | null = null
    glyphAnimInterval: any

    alwaysShowLabel: boolean
    suppressDetailHover: boolean

    tag: Graphics

    idleTimeout: any
    blinkingInterval: any

    chatBubble: Graphics
    chatBubbleHorizontalSpacer: Graphics
    bubbleText: DyanmicTextContainer
    chatBubbleTimeout: any
    chatBubbleFadeInterval: any
    damageInterval: any

    // --- Held speech (see spoke) -------------------------------------------------------------
    // The "Next" button, built once and reused; only parented/registered while a held line is
    // waiting on it.
    nextButton?: Container
    private nextButtonBg?: Graphics
    private nextButtonLabel?: Text
    private nextButtonHovered: boolean = false
    // The countdown bar drawn under the label (see drawNextProgress). Its own Graphics so a tick
    // repaints two pixels of bar instead of the whole button.
    private nextButtonProgress?: Graphics
    // The self-press that keeps a held line from stalling the scene, and the redraw that shows it
    // coming. Both live exactly as long as an armed button -- see armNextAutoAdvance.
    private nextAutoTimeout: any
    private nextProgressInterval: any
    private nextArmedAt: number = 0
    // Width the bar was last drawn at, so a tick that wouldn't move it draws nothing.
    private nextProgressWidth: number = -1
    // True from the moment a held line lands until its Next is pressed. While it is set, everything
    // this entity says queues rather than replacing what's on screen.
    holding: boolean = false
    // Lines waiting their turn behind the held bubble, oldest first. They inherit the hold, so the
    // player cycles through the whole backlog with the same button, and each carries its OWN ack --
    // the speaker is told about the line the player actually just read, not the one at the front.
    private speechQueue: { message: string; onDismissed?: Callback<void> }[] = []
    // Whether the line currently on screen was drawn instantly (PiP focus) -- carried so a queued
    // line is shown the same way.
    private lastInstant: boolean = false
    // The line on screen while `holding`, kept only to recognize a repeat of it (see spoke).
    private currentHeldMessage?: string
    // Completion callback of the line being held, run when its bubble finally closes.
    private holdDone?: Callback<void>
    // The held line's ack -- see SpeechOptions.onDismissed. Fired by the Next press, not by the
    // bubble closing: the player reading the line is the event the speaker is waiting on.
    private heldDismissedCallback?: Callback<void>

    pointerIcon: Graphics
    pointerIconPulseInterval: any
    pointerIconAngleInterval: any
    sigil: Graphics
    // chatQueue: JobQueue = new JobQueue()

    speaking: boolean

    constructor(
        color = 0xff00ff,
        renderMeta: EntityRenderMeta,
        parentArea: BaseRenderable,
        suppressDetailHover?: boolean,
    ) {
        super()
        this.parentArea = parentArea
        this.currentColor = color

        // Paint the HUD's widgets by zIndex (see the Z_* constants) rather than by addChild order.
        this.sortableChildren = true

        this.alwaysShowLabel = renderMeta.alwaysShowLabel

        this.idText = new TextRenderer().size("small").color(0xffffff).render() as Text
        // No backing plate (variant B): give the name a crisp black outline + soft shadow so it stays
        // legible over busy terrain.
        const nameStyle = this.idText.style
        nameStyle.stroke = "#000000"
        nameStyle.strokeThickness = 3
        nameStyle.lineJoin = "round"
        nameStyle.dropShadow = true
        nameStyle.dropShadowColor = "#000000"
        nameStyle.dropShadowAlpha = 0.55
        nameStyle.dropShadowBlur = 2
        nameStyle.dropShadowAngle = Math.PI / 2
        nameStyle.dropShadowDistance = 1

        // HP as a compact segmented meter drawn below the name (replaces the old "|" pipe text).
        this.hpBar = new Graphics()

        this.suppressDetailHover = suppressDetailHover
        const startVisible = !suppressDetailHover ? this.alwaysShowLabel : false
        this.idText.visible = startVisible
        this.hpBar.visible = startVisible
        this.idText.alpha = 1

        const tag = new Graphics()
        tag.beginFill(0xffffff)
        tag.lineStyle(1, 0xffffff)
        tag.drawCircle(-8, 8, 6)
        tag.endFill()
        tag.zIndex = Z_TAG
        this.addChild(tag)
        tag.visible = false
        tag.alpha = 0.7
        this.tag = tag

        this.idText.zIndex = Z_LABEL
        this.hpBar.zIndex = Z_LABEL
        this.addChild(this.idText)
        this.addChild(this.hpBar)

        this.update(3, 3)
        this.calcLabel()

        this.chatBubble = new Graphics()
        this.chatBubble.visible = true
        this.chatBubble.x = 0
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.addChild(this.chatBubble)

        const chatBubbleHorizontalSpacer = new Graphics()
        this.chatBubble.addChild(chatBubbleHorizontalSpacer)

        const bubbleText = this.makeBubbleText()
        this.chatBubble.addChild(bubbleText)
        this.chatBubble.visible = false

        const pointer = new BasicSprite({
            name: "icon-arrow.png",
            color: 0xffffff,
            scale: 1.0,
        })
        pointer.visible = false
        pointer.zIndex = Z_POINTER

        this.addChild(pointer)
        this.pointerIcon = pointer
    }

    makeBubbleText = () => {
        const bubbleText = new DyanmicTextContainer({
            // progressive is set per-utterance in spokeImpl (typewriter on the main view, instant in
            // the PiP focus), so this is just the default.
            progressive: true,
            // The styled panel drawn in spokeImpl is the bubble background now; the old inner
            // text-hugging backdrop just muddied it with a second dark rect, so it's disabled.
            backdrop: false,
        })
        bubbleText.x = BUBBLE_PAD_X
        bubbleText.y = BUBBLE_PAD_Y
        this.bubbleText = bubbleText

        return bubbleText
    }

    updateSigil(sigil?: string) {
        // remove existing sigil
        if (this.sigil) {
            this.sigil.parent?.removeChild(this.sigil)
        }

        if (sigil) {
            this.sigil = new BasicSprite({
                name: sigil,
                scale: 1.8,
                color: 0xffffff,
            })
            this.sigil.x += this.sigil.width * 0.3
            this.sigil.y = -3
            this.sigil.zIndex = Z_SIGIL
            this.addChild(this.sigil)
        }
    }

    // Show/replace the floating role glyph above the head (or remove it when the entity has no role).
    updateRole = (role: EmberRosterRole | null) => {
        if (this.currentRole === role) {
            return
        }
        this.currentRole = role

        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (this.roleGlyph) {
            this.removeChild(this.roleGlyph)
            this.roleGlyph.destroy({ texture: true, baseTexture: true })
            this.roleGlyph = undefined
        }

        if (role) {
            // No .color() -> tint stays the no-op white so the emoji keeps its own colours.
            const glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(ROLE_GLYPH[role]) as Text
            glyph.anchor.set(0.5)
            glyph.roundPixels = true
            glyph.x = GLYPH_X
            glyph.y = GLYPH_Y
            // Hidden while the label is showing (hovered / always-on), else visible above the head.
            const hidden = this.idText.visible || this.alwaysShowLabel
            glyph.alpha = hidden ? 0 : 1
            glyph.visible = !hidden
            glyph.zIndex = Z_ROLE_GLYPH
            this.addChild(glyph)
            this.roleGlyph = glyph
        }
    }

    // Fade the glyph in/out -- it hides on hover so the name/HP take its place, and fades back when
    // the pointer leaves. Runs a short self-clearing interval only while fading, so there's no
    // per-frame timer sitting idle.
    private fadeGlyphTo = (targetAlpha: number) => {
        const glyph = this.roleGlyph
        if (!glyph) {
            return
        }
        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (targetAlpha > 0) {
            glyph.visible = true // fade-in from a hidden glyph must start visible
        }
        const startAlpha = glyph.alpha
        const delta = targetAlpha - startAlpha
        if (Math.abs(delta) < 0.02) {
            glyph.alpha = targetAlpha
            glyph.visible = targetAlpha > 0
            return
        }
        const stepMs = 16
        let elapsed = 0
        this.glyphAnimInterval = setInterval(() => {
            elapsed += stepMs
            const t = Math.min(1, elapsed / GLYPH_FADE_MS)
            glyph.alpha = startAlpha + delta * t
            if (t >= 1) {
                glyph.alpha = targetAlpha
                glyph.visible = targetAlpha > 0
                clearInterval(this.glyphAnimInterval)
                this.glyphAnimInterval = undefined
            }
        }, stepMs)
    }

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color
        if (this.tag) {
            this.tag.tint = color
        }
    }

    updateLevel = (level: number) => {
        if (this.currentLevel === level) {
            return
        }
        this.currentLevel = level
        this.calcLabel()
    }

    updateName = (name: string) => {
        if (name !== this.currentName) {
            this.currentName = name
            this.calcLabel()
        }
    }

    update = (hp: number, hpMax: number) => {
        if (this.currentHp !== hp) {
            this.currentHp = hp
            this.calcLabel()
        }
        if (this.currentHpMax !== hpMax) {
            this.currentHpMax = hpMax
            this.calcLabel()
        }
    }

    private calcLabel = () => {
        let name = this.currentName || ""
        const max = 15
        if (name.length > max) {
            name = name.substring(0, max - 3) + "..."
        }
        const level: string = !!name && this.currentLevel > 0 ? `Lv ${this.currentLevel}` : ""

        this.idText.y = 0
        this.idText.text = (name + "  " + level).trim()

        // Left-align the HP meter to the name's left edge, just below it.
        this.hpBar.x = this.idText.x
        this.hpBar.y = this.idText.y + this.idText.height + 2
        this.drawHpBar()
    }

    private drawHpBar = () => {
        const g = this.hpBar
        g.clear()
        if (this.currentHpMax < 1) {
            return
        }

        const ratio = Math.max(0, Math.min(1, this.currentHp / this.currentHpMax))
        const color = ratio < 0.4 ? HP_COLOR_CRIT : ratio < 0.7 ? HP_COLOR_WARN : HP_COLOR_FULL

        const innerW = HP_BAR_W - HP_CASE_PAD * 2
        const segW = (innerW - HP_SEG_GAP * (HP_SEGMENTS - 1)) / HP_SEGMENTS
        const segH = HP_BAR_H - HP_CASE_PAD * 2
        // Keep at least one segment lit while alive so a near-dead entity never reads as empty.
        const lit = this.currentHp > 0 ? Math.max(1, Math.round(ratio * HP_SEGMENTS)) : 0

        // Dark casing
        g.beginFill(0x000000, 0.72)
        g.drawRoundedRect(0, 0, HP_BAR_W, HP_BAR_H, 2)
        g.endFill()

        // Notched segments
        for (let i = 0; i < HP_SEGMENTS; i++) {
            const on = i < lit
            g.beginFill(on ? color : 0xffffff, on ? 1 : 0.1)
            g.drawRoundedRect(HP_CASE_PAD + i * (segW + HP_SEG_GAP), HP_CASE_PAD, segW, segH, 1)
            g.endFill()
        }
    }

    addTag() {
        this.tag.visible = true
        this.tag.tint = this.currentColor
    }

    removeTag() {
        this.tag.visible = false
    }

    // Raise/lower this entity's world-overlay item so a live bubble clears the HUDs of neighbouring
    // entities (their nameplates and role glyphs are siblings of this HUD, not children of it).
    private liftOverlay = (speaking: boolean) => {
        const overlayItem = this.parent
        if (!overlayItem) {
            return
        }
        overlayItem.zIndex = speaking ? Z_OVERLAY_SPEAKING : Z_OVERLAY_RESTING
    }

    // A line this entity just said. See SpeechOptions for what the flags mean.
    //
    // A HELD line's bubble waits for a "Next" press instead of fading on a timer sized to the word
    // count -- players were losing the ember Lord's intro to that timer. The press also calls the
    // line's `onDismissed`, which is how the SPEAKER gets to pace itself off the player's reading
    // speed rather than its own clock. If nobody presses, the button presses itself after
    // NEXT_BUTTON_AUTO_MS, so handing the pacing to the player can never stall a scene outright.
    //
    // Anything this entity says while a held line is up QUEUES behind it and is shown, in order, as
    // the player works through the backlog with the same button, so no line can be shouldered off
    // the screen before it has been read. A speaker that waits for the ack keeps that queue empty by
    // construction; it is the safety net for one that doesn't (and for a line from somewhere else
    // entirely, mid-conversation). Only this entity's lines queue -- every speaker has its own HUD.
    spoke = (message: string, options?: SpeechOptions) => {
        const instant = !!options?.instant
        const hold = !!options?.hold
        const onDismissed = options?.onDismissed

        if (this.holding) {
            // Don't stack a line that's already on screen or already waiting -- a nudge the script
            // re-asks on a timer ("The wood. Onto the fire.") would otherwise become a stack of
            // identical bubbles for the player to click through.
            const lastQueued = this.speechQueue[this.speechQueue.length - 1]
            if (
                message === lastQueued?.message ||
                (!this.speechQueue.length && message === this.currentHeldMessage)
            ) {
                return
            }
            this.speechQueue.push({ message, onDismissed })
            while (this.speechQueue.length > MAX_QUEUED_LINES) {
                this.speechQueue.shift()
            }
            // The button carries the backlog count, so the player can see there's more behind it.
            this.drawNextButton()
            return
        }

        this.showLine(message, instant, hold, onDismissed)
    }

    // Put one line on screen, replacing whatever is there. The queue is handled by spoke(); by the
    // time we get here this line is the one to show.
    private showLine = (message: string, instant: boolean, hold: boolean, onDismissed?: Callback<void>) => {
        if (this.speaking) {
            this.bubbleText.wipe()
            this.bubbleText.parent.removeChild(this.bubbleText)

            this.bubbleText = this.makeBubbleText()
            this.chatBubble.addChild(this.bubbleText)
        }

        this.speaking = true
        this.holding = hold
        this.lastInstant = instant
        this.currentHeldMessage = hold ? message : undefined
        this.heldDismissedCallback = hold ? onDismissed : undefined
        this.hideNextButton()
        this.spokeImpl(message, instant, hold, () => {
            this.speaking = false
        })
    }

    // How many of this entity's lines are waiting behind the held one. Exposed for tests and for the
    // button's own label.
    queuedSpeechCount = () => this.speechQueue.length

    // Take a held bubble down WITHOUT the player pressing "Next", because the conversation it
    // belonged to has been called off -- the ember tutorial's Skip. The backlog goes with it (those
    // lines are the rest of a scene nobody is playing now), and without this the sign-off that
    // follows a skip would queue behind the very button the player just declined, inherit its hold,
    // and sit there unread.
    //
    // The speaker is deliberately NOT acked: nothing here was read, and a script pacing itself off
    // the ack falls back to its own timeout, exactly as it does for a bubble that was never drawn.
    releaseHeldSpeech = () => {
        if (!this.holding && !this.speechQueue.length) {
            return
        }
        this.speechQueue = []
        this.holding = false
        this.currentHeldMessage = undefined
        this.heldDismissedCallback = undefined
        this.hideNextButton()
        this.closeBubble()
    }

    // "Next" pressed -- by the player, or by the button's own countdown running out. Tells the
    // speaker it may go on, then shows the next line in the backlog, or closes the bubble when there
    // is none.
    advanceHeldSpeech = () => {
        if (!this.holding) {
            return
        }
        this.holding = false
        this.currentHeldMessage = undefined
        this.hideNextButton()

        // Ack the line the player just read BEFORE showing the next one: a speaker waiting on this
        // is holding its own tongue until it arrives.
        const dismissed = this.heldDismissedCallback
        this.heldDismissedCallback = undefined
        dismissed?.()

        const next = this.speechQueue.shift()
        if (next) {
            // Queued lines inherit the hold: the player has taken over the pacing of this
            // conversation, so every line of it waits for them.
            this.showLine(next.message, this.lastInstant, true, next.onDismissed)
            return
        }

        this.closeBubble()
    }

    spokeImpl = (message: string, instant: boolean, hold: boolean, done: Callback<void>) => {
        if (this.chatBubbleTimeout) {
            clearTimeout(this.chatBubbleTimeout)
            this.chatBubbleTimeout = undefined
        }
        if (this.chatBubbleFadeInterval) {
            clearInterval(this.chatBubbleFadeInterval)
            this.chatBubbleFadeInterval = undefined
        }
        this.hpBar.visible = false
        this.idText.visible = false
        // A chat bubble takes over the label area; the glyph returns to its resting visible state.
        this.fadeGlyphTo(1)

        const wordCount = message.split(" ").length
        const delayMs = Math.max(3000, ((wordCount * 60) / 200) * 1250)

        const sentences = chunkSentence(message, 22)

        // Size the bubble to the ACTUAL rendered text rather than a per-char estimate -- the old
        // estimate under-sized the panel, so text crowded the right/top edges. Measure the wrapped
        // lines with the real bubble font and wrap the panel around them with uniform padding.
        const probe = new TextRenderer().variant("BubbleChatFont").render() as Text
        const metrics = TextMetrics.measureText(sentences.join("\n"), probe.style as any)
        probe.destroy({ texture: true, baseTexture: true })
        // A held line needs a row at the bottom for its Next button -- grow the panel around it the
        // same way the text grows it, so the button is always BELOW the words and never over them,
        // however they wrapped. (Same lesson as the tutorial bar: buttons beside server-authored
        // copy only fits until the copy gets a few words longer.)
        const textH = Math.ceil(metrics.height)
        const buttonRowH = hold ? NEXT_BUTTON_GAP + NEXT_BUTTON_H : 0
        const minW = hold ? NEXT_BUTTON_W + BUBBLE_PAD_X * 2 : 46
        const bubbleW = Math.max(minW, Math.ceil(metrics.width) + BUBBLE_PAD_X * 2)
        const bubbleH = textH + buttonRowH + BUBBLE_PAD_Y * 2

        this.bubbleText.x = BUBBLE_PAD_X
        this.bubbleText.y = BUBBLE_PAD_Y

        // Trace the whole bubble outline -- rounded rect plus a tail on the bottom edge -- as one
        // continuous path. Filling and stroking a single path keeps the border seamless around the
        // tail and the fill uniform (drawing the tail as a separate shape left a seam + colour gap).
        const r = BUBBLE_RADIUS
        const tailBaseLeftX = 14
        const tailBaseRightX = 30
        const tailTipX = 6
        const tailTipY = bubbleH + 14
        const tracePath = (ox: number, oy: number) => {
            const g = this.chatBubble
            g.moveTo(ox + r, oy)
            g.lineTo(ox + bubbleW - r, oy)
            g.quadraticCurveTo(ox + bubbleW, oy, ox + bubbleW, oy + r)
            g.lineTo(ox + bubbleW, oy + bubbleH - r)
            g.quadraticCurveTo(ox + bubbleW, oy + bubbleH, ox + bubbleW - r, oy + bubbleH)
            g.lineTo(ox + tailBaseRightX, oy + bubbleH)
            g.lineTo(ox + tailTipX, oy + tailTipY) // out to the tail tip, pointing down toward the entity
            g.lineTo(ox + tailBaseLeftX, oy + bubbleH)
            g.lineTo(ox + r, oy + bubbleH)
            g.quadraticCurveTo(ox, oy + bubbleH, ox, oy + bubbleH - r)
            g.lineTo(ox, oy + r)
            g.quadraticCurveTo(ox, oy, ox + r, oy)
            g.closePath()
        }

        this.chatBubble.clear()
        this.chatBubble.alpha = 1.0
        this.chatBubble.x = TileSize * 1.4
        this.chatBubble.zIndex = Z_CHAT_BUBBLE
        this.liftOverlay(true)

        // Soft drop shadow (same silhouette, offset, no stroke) for depth against the busy world
        this.chatBubble.lineStyle(0)
        this.chatBubble.beginFill(0x000000, 0.3)
        tracePath(BUBBLE_SHADOW_X, BUBBLE_SHADOW_Y)
        this.chatBubble.endFill()

        // Panel body + tail: one path, filled and bordered together for a seamless outline
        this.chatBubble.lineStyle({
            width: BUBBLE_BORDER,
            color: BUBBLE_BORDER_COLOR,
            alpha: BUBBLE_BORDER_ALPHA,
            join: LINE_JOIN.ROUND,
        })
        this.chatBubble.beginFill(BUBBLE_FILL, BUBBLE_FILL_ALPHA)
        tracePath(0, 0)
        this.chatBubble.endFill()

        this.chatBubble.visible = true

        this.bubbleText.progressive = !instant
        this.bubbleText.wipe()
        sentences.forEach(sentence => {
            this.bubbleText.append2(sentence, {
                font: "BubbleChatFont",
            })
        })

        const scheduleFade = () => {
            this.chatBubbleTimeout = setTimeout(() => {
                const fader: Fader = new Fader(this.chatBubble, {
                    releaseCallback: () => {
                        clearInterval(this.chatBubbleFadeInterval)
                        this.bubbleText.wipe()
                        this.chatBubble.visible = false
                        this.chatBubble.alpha = 1.0
                        this.liftOverlay(false)

                        done()
                    },
                })
                fader.update()
            }, delayMs * 0.5)
        }

        // A held line hands the dismissal to the player: arm the button once the words have all
        // arrived (no point offering "Next" over a half-typed sentence, nor starting its countdown
        // while they're still appearing) and schedule no fade at all.
        const finish = hold
            ? () => {
                  this.holdDone = done
                  this.showNextButton(bubbleW, bubbleH)
              }
            : scheduleFade

        if (instant) {
            // PiP focus: the line is already fully drawn (non-progressive above), so just fade — no
            // typewriter, whose progressive-reveal objects flicker when rendered into the PiP texture.
            finish()
        } else {
            this.bubbleText.playback(finish)
        }
    }

    // Take the bubble down for good: no fade, because the player has just told us they're done with
    // it and a dismissal that lingers reads as an unresponsive button.
    private closeBubble = () => {
        if (this.chatBubbleTimeout) {
            clearTimeout(this.chatBubbleTimeout)
            this.chatBubbleTimeout = undefined
        }
        if (this.chatBubbleFadeInterval) {
            clearInterval(this.chatBubbleFadeInterval)
            this.chatBubbleFadeInterval = undefined
        }
        this.bubbleText.cleanup()
        this.bubbleText.wipe()
        this.chatBubble.visible = false
        this.chatBubble.alpha = 1.0
        this.liftOverlay(false)

        const done = this.holdDone
        this.holdDone = undefined
        done?.()
    }

    // ---------------------------------------------------------------------------------------
    // The "Next" button on a held bubble.
    // ---------------------------------------------------------------------------------------

    private makeNextButton = (): Container => {
        const button = new Container()
        button.interactive = true
        button.cursor = "pointer"
        button.hitArea = new Rectangle(0, 0, NEXT_BUTTON_W, NEXT_BUTTON_H)

        const bg = new Graphics()
        button.addChild(bg)
        this.nextButtonBg = bg

        // Added between the background and the label so the bar reads as part of the button's rim
        // rather than as something sitting on top of the words.
        const progress = new Graphics()
        button.addChild(progress)
        this.nextButtonProgress = progress

        const label = new TextRenderer().color(NEXT_TEXT).fontSize(NEXT_BUTTON_FONT).bold().align("center").render("")
        button.addChild(label)
        this.nextButtonLabel = label as Text

        button.on("pointerover", () => {
            this.nextButtonHovered = true
            this.drawNextButton()
        })
        button.on("pointerout", () => {
            this.nextButtonHovered = false
            this.drawNextButton()
        })
        button.on("pointerdown", (event: any) => {
            event?.stopPropagation?.()
            // The world-attack click comes from a document-level mousedown that can't see this
            // widget, so ask the player controller to hold off -- same deal as an interaction icon.
            SpeechHoldButtons.notePressed()
            this.advanceHeldSpeech()
        })

        return button
    }

    private drawNextButton = () => {
        const bg = this.nextButtonBg
        const label = this.nextButtonLabel
        if (!bg || !label) {
            return
        }

        // The backlog count is the only cue that there is more waiting, so it lives on the button.
        const pending = this.speechQueue.length
        label.text = pending > 0 ? `Next ${pending + 1}›` : "Next ›"
        label.x = Math.round(NEXT_BUTTON_W / 2 - label.width / 2)
        // Centred in the space ABOVE the countdown bar, not in the whole button.
        label.y = Math.round((NEXT_BUTTON_H - NEXT_PROGRESS_ROW) / 2 - label.height / 2)

        bg.clear()
        if (this.nextButtonHovered) {
            bg.beginFill(NEXT_RIM, 0.16)
            bg.drawRoundedRect(-3, -3, NEXT_BUTTON_W + 6, NEXT_BUTTON_H + 6, NEXT_BUTTON_CORNER + 3)
            bg.endFill()
            bg.lineStyle(1.5, NEXT_RIM, 1)
            bg.beginFill(NEXT_FILL_HOVER, 0.97)
        } else {
            bg.lineStyle(1.5, NEXT_RIM_IDLE, 0.85)
            bg.beginFill(NEXT_FILL_IDLE, 0.92)
        }
        bg.drawRoundedRect(0, 0, NEXT_BUTTON_W, NEXT_BUTTON_H, NEXT_BUTTON_CORNER)
        bg.endFill()
    }

    private showNextButton = (bubbleW: number, bubbleH: number) => {
        if (!this.nextButton) {
            this.nextButton = this.makeNextButton()
        }
        const button = this.nextButton
        // Bottom-right inside the panel; the tail hangs off the bottom-LEFT, so the two never meet.
        button.x = bubbleW - BUBBLE_PAD_X - NEXT_BUTTON_W
        button.y = bubbleH - BUBBLE_PAD_Y - NEXT_BUTTON_H
        button.visible = true
        this.nextButtonHovered = false
        this.drawNextButton()

        if (button.parent !== this.chatBubble) {
            this.chatBubble.addChild(button)
        }
        // Only a button the player can actually reach is registered -- the registry is what the
        // follow-cam hides while it composites the overlay into its texture.
        SpeechHoldButtons.register(button)

        this.armNextAutoAdvance()
    }

    // Start this line's countdown. When it runs out the button takes exactly the path a press takes
    // -- same ack, same backlog cycling -- so the speaker cannot tell the two apart, and a scene
    // paced off the ack keeps moving whether or not anyone is watching it.
    private armNextAutoAdvance = () => {
        this.clearNextAutoAdvance()
        this.nextArmedAt = Date.now()
        this.drawNextProgress()
        this.nextProgressInterval = setInterval(this.drawNextProgress, NEXT_PROGRESS_TICK_MS)
        this.nextAutoTimeout = setTimeout(() => {
            this.nextAutoTimeout = undefined
            this.advanceHeldSpeech()
        }, NEXT_BUTTON_AUTO_MS)
    }

    private clearNextAutoAdvance = () => {
        if (this.nextAutoTimeout) {
            clearTimeout(this.nextAutoTimeout)
            this.nextAutoTimeout = undefined
        }
        if (this.nextProgressInterval) {
            clearInterval(this.nextProgressInterval)
            this.nextProgressInterval = undefined
        }
        this.nextButtonProgress?.clear()
        this.nextProgressWidth = -1
    }

    // The countdown, as a thin amber bar along the bottom inside edge of the button that drains from
    // full to nothing. Time is read off the clock rather than counted in ticks so a throttled
    // background tab (or a dropped frame) leaves the bar honest about the timeout it is tracking.
    private drawNextProgress = () => {
        const bar = this.nextButtonProgress
        if (!bar) {
            return
        }
        const remaining = 1 - (Date.now() - this.nextArmedAt) / NEXT_BUTTON_AUTO_MS
        const full = NEXT_BUTTON_W - NEXT_PROGRESS_INSET * 2
        const width = Math.max(0, Math.round(full * Math.min(1, remaining)))
        if (width === this.nextProgressWidth) {
            return
        }
        this.nextProgressWidth = width
        bar.clear()
        if (width <= 0) {
            return
        }
        bar.beginFill(NEXT_RIM, NEXT_PROGRESS_ALPHA)
        bar.drawRect(NEXT_PROGRESS_INSET, NEXT_BUTTON_H - NEXT_PROGRESS_ROW, width, NEXT_PROGRESS_H)
        bar.endFill()
    }

    private hideNextButton = () => {
        this.clearNextAutoAdvance()
        const button = this.nextButton
        if (!button) {
            return
        }
        SpeechHoldButtons.unregister(button)
        button.visible = false
        this.nextButtonHovered = false
        button.parent?.removeChild(button)
    }

    // The entity is going away (removed, or culled and discarded). Drop the HUD's own timers and,
    // above all, take any live Next button out of the shared registry -- a button left in there
    // belongs to a HUD nobody can click any more, and the follow-cam would keep toggling it forever.
    cleanup() {
        if (this.chatBubbleTimeout) {
            clearTimeout(this.chatBubbleTimeout)
            this.chatBubbleTimeout = undefined
        }
        if (this.chatBubbleFadeInterval) {
            clearInterval(this.chatBubbleFadeInterval)
            this.chatBubbleFadeInterval = undefined
        }
        if (this.glyphAnimInterval) {
            clearInterval(this.glyphAnimInterval)
            this.glyphAnimInterval = undefined
        }
        if (this.pointerIconPulseInterval) {
            clearInterval(this.pointerIconPulseInterval)
            this.pointerIconPulseInterval = undefined
        }
        if (this.pointerIconAngleInterval) {
            clearInterval(this.pointerIconAngleInterval)
            this.pointerIconAngleInterval = undefined
        }
        this.hideNextButton()
        this.speechQueue = []
        this.holding = false
        this.currentHeldMessage = undefined
        this.holdDone = undefined
        // Deliberately NOT fired: the HUD is being discarded, which is not the player reading
        // anything. A speaker waiting on the ack falls back to its own timeout instead.
        this.heldDismissedCallback = undefined
    }

    onMouseOver = (show: boolean) => {
        if (!this.suppressDetailHover && !this.speaking) {
            if (!this.alwaysShowLabel) {
                this.idText.visible = show
                this.hpBar.visible = show
                this.fadeGlyphTo(show ? 0 : 1)
            }

            if (show) {
                this.calcLabel()
            }
        }
    }

    updatePointerIcon(show: boolean, angle?: number) {
        if (!show) {
            clearInterval(this.pointerIconPulseInterval)
            clearInterval(this.pointerIconAngleInterval)
            this.pointerIconPulseInterval = undefined
            this.pointerIconAngleInterval = undefined
            this.pointerIcon.visible = false
            this.pointerIcon.angle = 0
        } else if (angle !== undefined) {
            this.pointerIcon.visible = true

            this.pointerIcon.angle = angle
            const radius = 32
            const x = radius * Math.sin((Math.PI * 2 * 90) / 360)
            const y = radius * Math.cos((Math.PI * 2 * 90) / 360)
            this.pointerIcon.x = x + radius - 10
            this.pointerIcon.y = y + radius + 10

            if (!this.pointerIconPulseInterval) {
                let scale = 1.0
                let scaleDir = 1

                this.pointerIconPulseInterval = setInterval(() => {
                    scale += 0.01 * scaleDir
                    if (scale > 1.2 || scale < 1.0) {
                        scaleDir = scaleDir * -1
                    }
                    this.pointerIcon.scale.set(scale)
                }, 50)
            }
        }
    }
}
